import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Link } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowBox } from '~/components/common/arrow-box';
import { CompetitionCell, type CompetitionBand } from '~/components/common/competition-cell';
import { InfoTooltip } from '~/components/common/info-tooltip/info-tooltip';
import { PremiumBadge } from '~/components/common/premium-badge';
import { SearchInput } from '~/components/common/search-input';
import { GoogleLogo } from '~/components/logos/GoogleLogo';
import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/utils/cn';
import { formatCompactNumber } from '~/utils/formatting';

/** Table rows beyond this count reveal a product-term search box. */
const SEARCH_THRESHOLD = 5;

/** Column fields sortable via the header click — column index → sort field. */
type SortField = 'term' | 'monthlySearchCount' | 'competitionIndex';
const SORT_FIELDS_BY_COLUMN: Partial<Record<number, SortField>> = {
  0: 'term',
  1: 'monthlySearchCount',
  2: 'competitionIndex',
};

interface SortState {
  field: SortField | null;
  direction: 'asc' | 'desc';
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AIPerformanceInsight {
  /** Product term label. */
  term: string;
  /** Raw monthly search count — formatted to K/M/B locally. */
  monthlySearchCount: number;
  impressionShare: {
    /** Pre-formatted before percentage string, e.g. "3.2%". */
    beforePercent: string;
    /** Pre-formatted after percentage string, e.g. "14.6%". */
    afterPercent: string;
  };
  terms: {
    /** Number of products containing the term before enrichment. */
    beforeCount: number;
    /** Number of products containing the term after enrichment. */
    afterCount: number;
  };
  /** Keyword Planner competition band + index (0–100). `null` when Google
   *  returns neither for the term. */
  competition: CompetitionBand;
}

interface AIPerformanceInsightsProps {
  /** Section heading. Pre-translated by the caller. */
  title: string;
  /** Section sub-heading. Pre-translated by the caller. */
  subtitle: string;
  /** Table sub-section label. Pre-translated by the caller. */
  tableTitle: string;
  /** Table sub-section description. Pre-translated by the caller. */
  tableDescription: string;
  /** When provided, renders an info tooltip next to the table description. Pre-translated by the caller. */
  tableDescriptionTooltip?: string;
  /** Ordered list of performance insight rows. */
  items: AIPerformanceInsight[];
  /** When true, hides the Before/After Enrichment colour legend. */
  hideLegends?: boolean;
  /** When true, renders the Google "G" logo before the title. */
  showLogo?: boolean;
  /**
   * 0-based column indices to render as premium-locked: header shows a crown
   * icon and cell values are grayed out to indicate a paid-only feature.
   */
  premiumFeatures?: number[];
  /** When true, drops the Impression Share column entirely — the backend
   *  doesn't populate real values for it yet, so every row reads a flat 0.00%
   *  rather than a meaningful before/after. */
  hideImpressionShare?: boolean;
  /** Whether the underlying Google Ads connection is active. When `false` and
   *  there's no data, the empty state prompts the user to connect instead of
   *  showing the generic "no data yet" message. Defaults to `true` so callers
   *  that don't track connection status keep the original empty state. */
  isGoogleAdsConnected?: boolean;
  /** When provided, rows become clickable (cursor-pointer, keyboard-operable)
   *  and call this with the row's term on click/Enter/Space — opens the
   *  term-products drawer. Rows stay inert (hover highlight only) when omitted. */
  onRowClick?: (item: AIPerformanceInsight) => void;
  /** `data-testid` root; derives `-search`, `-table`, `-sort-{field}`, `-empty`, `-row-{term-slug}` (+ cell values). */
  testId?: string;
}

/** Kebab-cases a search term so it can key a `data-testid`. */
function toTestIdSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Delta chip: green (success-50 bg) for positive delta, brand-50 bg +
 * destructive text for negative. Matches ScoreComparisonBar's DeltaChip spec.
 * When muted, renders with neutral colours for locked-column styling.
 */
function DeltaChip({ delta, muted = false }: { delta: number; muted?: boolean }) {
  const positive = delta >= 0;
  const Icon = positive ? ArrowUp : ArrowDown;
  const sign = positive ? '+' : '';

  if (muted) {
    return (
      <div className="bg-muted flex items-center gap-1 rounded-md px-2 py-0.5">
        <Icon className="text-muted-foreground h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="text-muted-foreground text-xs font-semibold">
          {sign}
          {delta}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex items-center gap-1 rounded-md px-2 py-0.5',
        positive ? 'bg-success-50' : 'bg-brand-50',
      )}
    >
      <Icon
        className={cn('h-3 w-3 shrink-0', positive ? 'text-success' : 'text-destructive')}
        aria-hidden="true"
      />
      <span className={cn('text-xs font-semibold', positive ? 'text-success' : 'text-destructive')}>
        {sign}
        {delta}
      </span>
    </div>
  );
}

/** Google "G" logo in a rounded, light-background square — shown before the
 *  section title when the insights being surfaced mirror a Google product. */
function GoogleLogoBadge() {
  return (
    <span className="bg-surface-subtle flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
      <GoogleLogo className="h-5 w-5" />
    </span>
  );
}

/**
 * Clickable column header label that toggles asc/desc sort on `field`.
 * Shows a neutral swap icon when inactive, and a directional arrow once the
 * column is the active sort key.
 */
function SortableColumnHeader({
  label,
  field,
  sort,
  onSortChange,
  testId,
}: {
  label: string;
  field: SortField;
  sort: SortState;
  onSortChange: (field: SortField) => void;
  testId: string;
}) {
  const isActive = sort.field === field;
  const Icon = isActive ? (sort.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn(
        'h-auto cursor-pointer gap-1 p-0 text-xs hover:bg-transparent',
        isActive
          ? 'text-primary hover:text-primary font-medium'
          : 'text-muted-foreground hover:text-foreground font-normal',
      )}
      onClick={() => {
        onSortChange(field);
      }}
      aria-label={`Sort by ${label}`}
      data-testid={testId}
    >
      {label}
      <Icon className={cn('h-3.5 w-3.5', isActive ? 'text-primary' : 'text-muted-foreground')} />
    </Button>
  );
}

/** Legend pill matching AttributeScoreList's LegendItem style. */
function LegendItem({ color, label }: { color: 'before' | 'after'; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span
        aria-hidden="true"
        className={cn(
          'h-3 w-3 shrink-0 rounded-full',
          color === 'before' ? 'bg-warning' : 'bg-primary',
        )}
      />
      <span className="text-text-date-pill text-sm">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * AI Performance Insights section: a header with an optional colour legend,
 * a labelled sub-section, and a rounded table showing term-level before/after
 * impression share and product counts with delta chips.
 * Columns listed in premiumFeatures are rendered with a crown icon in the
 * header and grayed-out cell values to indicate a paid-only feature.
 */
export function AIPerformanceInsights({
  title,
  subtitle,
  tableTitle,
  tableDescription,
  tableDescriptionTooltip,
  items,
  hideLegends = false,
  showLogo = false,
  premiumFeatures,
  isGoogleAdsConnected = true,
  hideImpressionShare = false,
  onRowClick,
  testId = 'ai-performance-insights',
}: AIPerformanceInsightsProps) {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState>({ field: null, direction: 'asc' });

  const showSearch = items.length > SEARCH_THRESHOLD;
  const filteredItems = useMemo(() => {
    if (!showSearch || !search.trim()) return items;
    const query = search.trim().toLowerCase();
    return items.filter((item) => item.term.toLowerCase().includes(query));
  }, [items, search, showSearch]);

  const sortedItems = useMemo(() => {
    if (!sort.field) return filteredItems;
    const dir = sort.direction === 'asc' ? 1 : -1;
    if (sort.field === 'competitionIndex') {
      // Rows with no competition data (Google returned none for the term)
      // sink to the bottom regardless of sort direction, rather than
      // treating "not available" as a 0.
      return [...filteredItems].sort((a, b) => {
        const av = a.competition?.index;
        const bv = b.competition?.index;
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return (av - bv) * dir;
      });
    }
    return [...filteredItems].sort((a, b) =>
      sort.field === 'term'
        ? a.term.localeCompare(b.term) * dir
        : (a.monthlySearchCount - b.monthlySearchCount) * dir,
    );
  }, [filteredItems, sort]);

  function handleSortChange(field: SortField) {
    setSort((prev) =>
      prev.field === field
        ? { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { field, direction: 'asc' },
    );
  }

  const lockedColumns = new Set(premiumFeatures ?? []);

  // Fixed column order — index 4 (Impression Share) always gets an explanatory
  // tooltip, and is dropped entirely when `hideImpressionShare` is set.
  const columnLabels = [
    t('aiPerformanceInsights.columns.term'),
    t('aiPerformanceInsights.columns.monthlySearches'),
    t('aiPerformanceInsights.columns.competition'),
    t('aiPerformanceInsights.columns.productsWithTerm'),
    ...(hideImpressionShare ? [] : [t('aiPerformanceInsights.columns.impressionShare')]),
  ];
  const impressionShareColumnIndex = 4;

  return (
    <div data-testid={testId} className="flex flex-col gap-4">
      {/* Section header */}
      <div className="flex items-start justify-between gap-6">
        <div className="flex items-start gap-3">
          {showLogo && <GoogleLogoBadge />}
          <div className="flex flex-col gap-1">
            <div className="flex flex-row items-center justify-between gap-4">
              <h2 data-testid={`${testId}-title`} className="text-foreground text-lg font-medium">
                {title}
              </h2>
              {!hideLegends && (
                <div className="flex shrink-0 items-center gap-4">
                  <LegendItem color="before" label={t('aiPerformanceInsights.legend.before')} />
                  <LegendItem color="after" label={t('aiPerformanceInsights.legend.after')} />
                </div>
              )}
            </div>
            <p className="text-muted-foreground text-sm font-light">{subtitle}</p>
          </div>
        </div>
      </div>

      <div className="border-border border-b" />

      {/* Table sub-section header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <p data-testid={`${testId}-table-title`} className="text-foreground text-md font-medium">
            {tableTitle}
          </p>
          <div className="flex items-center gap-1.5">
            <p className="text-muted-foreground text-sm font-light">{tableDescription}</p>
            {tableDescriptionTooltip && <InfoTooltip content={tableDescriptionTooltip} />}
          </div>
        </div>
        {showSearch && (
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t('aiPerformanceInsights.searchPlaceholder')}
            className="w-64 shrink-0"
            testId={`${testId}-search`}
          />
        )}
      </div>

      {/* Table — capped height with a scrollable body; the header row stays pinned via sticky positioning. */}
      <div className="max-h-85 overflow-x-hidden overflow-y-scroll rounded-[16px]">
        <table data-testid={`${testId}-table`} className="w-full">
          <thead className="sticky top-0 z-10">
            <tr className="bg-exp-status-decision-ready-bg">
              {columnLabels.map((label, colIndex) => {
                const showBadge = lockedColumns.has(colIndex);
                const isImpressionShare = colIndex === impressionShareColumnIndex;
                const sortField = SORT_FIELDS_BY_COLUMN[colIndex];
                const labelNode = sortField ? (
                  <SortableColumnHeader
                    label={label}
                    field={sortField}
                    sort={sort}
                    onSortChange={handleSortChange}
                    testId={`${testId}-sort-${sortField}`}
                  />
                ) : (
                  <span>{label}</span>
                );
                return (
                  <th
                    key={colIndex}
                    className="text-muted-foreground px-4 py-3 text-left text-xs font-normal"
                  >
                    {showBadge ? (
                      <div className="flex items-center gap-1.5">
                        {labelNode}
                        {isImpressionShare ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex cursor-help">
                                <PremiumBadge />
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-xs">
                              {t('aiPerformanceInsights.columns.impressionShareTooltip')}
                              <TooltipPrimitive.Arrow className="fill-popover" />
                            </TooltipContent>
                          </Tooltip>
                        ) : (
                          <PremiumBadge />
                        )}
                      </div>
                    ) : (
                      labelNode
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody className="divide-border divide-y">
            {sortedItems.length === 0 && (
              <tr data-testid={`${testId}-empty`}>
                <td
                  colSpan={columnLabels.length}
                  className="bg-surface-blue px-4 py-10 text-center"
                >
                  {items.length === 0 && !isGoogleAdsConnected ? (
                    <div className="flex flex-col items-center gap-2">
                      <p className="text-muted-foreground text-sm">
                        {t('aiPerformanceInsights.notConnected.message')}
                      </p>
                      <Link
                        data-testid={`${testId}-connect-link`}
                        to="/onboarding/integration/google/google-ads"
                        className="text-primary cursor-pointer text-sm font-medium underline underline-offset-2"
                      >
                        {t('aiPerformanceInsights.notConnected.cta')}
                      </Link>
                    </div>
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      {items.length === 0
                        ? t('aiPerformanceInsights.empty')
                        : t('aiPerformanceInsights.noSearchResults')}
                    </p>
                  )}
                </td>
              </tr>
            )}
            {sortedItems.map((item, index) => {
              const rowTestId = `${testId}-row-${toTestIdSlug(item.term)}`;
              const termsDelta = item.terms.afterCount - item.terms.beforeCount;
              const col3Locked = lockedColumns.has(3);
              const col4Locked = lockedColumns.has(4);
              return (
                <tr
                  key={index}
                  data-testid={rowTestId}
                  className={cn(
                    'bg-surface-blue hover:bg-muted/50 transition-colors',
                    onRowClick && 'group cursor-pointer',
                  )}
                  {...(onRowClick && {
                    role: 'button',
                    tabIndex: 0,
                    onClick: () => {
                      onRowClick(item);
                    },
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onRowClick(item);
                      }
                    },
                  })}
                >
                  {/* Product term — a chevron fades in on row hover/focus as a
                      "click to open" affordance, only when the row is actually
                      clickable (onRowClick provided). */}
                  <td
                    className={cn(
                      'px-4 py-4 text-sm font-normal',
                      lockedColumns.has(0) ? 'text-muted' : 'text-foreground',
                    )}
                  >
                    <span className="inline-flex items-center gap-2">
                      {item.term}
                      {onRowClick && (
                        <ChevronRight
                          className="text-muted-foreground h-3.5 w-3.5 shrink-0 opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                          aria-hidden="true"
                        />
                      )}
                    </span>
                  </td>

                  {/* Monthly searches */}
                  <td
                    data-testid={`${rowTestId}-monthly-searches`}
                    className={cn(
                      'px-4 py-4 text-sm font-light',
                      lockedColumns.has(1) ? 'text-muted' : 'text-foreground',
                    )}
                  >
                    {formatCompactNumber(item.monthlySearchCount)}
                  </td>

                  {/* Competition: band + index, or "Not available" */}
                  <td className="px-4 py-4">
                    <CompetitionCell competition={item.competition} />
                  </td>

                  {/* Products with term: before → after + delta chip */}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <span
                        data-testid={`${rowTestId}-products-before`}
                        className={cn(
                          'text-sm font-medium',
                          col3Locked ? 'text-muted-text' : 'text-warning',
                        )}
                      >
                        {item.terms.beforeCount}
                      </span>
                      <ArrowBox muted={col3Locked} />
                      <span
                        data-testid={`${rowTestId}-products-after`}
                        className={cn(
                          'text-sm font-medium',
                          col3Locked ? 'text-muted-text' : 'text-primary',
                        )}
                      >
                        {item.terms.afterCount}
                      </span>
                      <DeltaChip delta={termsDelta} muted={col3Locked} />
                    </div>
                  </td>

                  {/* Impression share: before → after */}
                  {!hideImpressionShare && (
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <span
                          data-testid={`${rowTestId}-impression-before`}
                          className={cn(
                            'text-sm font-medium',
                            col4Locked ? 'text-muted-text' : 'text-warning',
                          )}
                        >
                          {item.impressionShare.beforePercent}
                        </span>
                        <ArrowBox muted={col4Locked} />
                        <span
                          data-testid={`${rowTestId}-impression-after`}
                          className={cn(
                            'text-sm font-medium',
                            col4Locked ? 'text-muted-text' : 'text-primary',
                          )}
                        >
                          {item.impressionShare.afterPercent}
                        </span>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
